import { ReactNode, useId } from 'react';
import { bandOf } from './score';
import { useI18n } from '../i18n';
import { VisuallyHidden } from './a11y';

export function Pill({ kind, children }: { kind: 'crit' | 'warn' | 'good' | 'neutral'; children: ReactNode }) {
  return <span className={`pill pill-${kind}`}>{children}</span>;
}

// A 1–5 score: numeral coloured by band, "/5" shown visually, "out of 5"
// spoken. Large bold numerals only (≥ 24px), where the band colours pass 3:1.
export function ScoreValue({ score, className = 'kpi-value' }: { score: number | null; className?: string }) {
  const { t, num } = useI18n();
  if (score === null) return <p className={className}>–</p>;
  const band = bandOf(score);
  return (
    <p className={className} style={{ color: band.color }}>
      {num(score)}
      <small aria-hidden="true">/5</small>
      <VisuallyHidden> {t.outOf5}</VisuallyHidden>
    </p>
  );
}

// StatTile: caption label, 30px bold value (coloured by score band when it's
// a 1–5 score), optional sub-line. Label and value are a <dl>-style pair for
// screen readers via role="group" + aria-labelledby.
export function StatTile({
  label,
  value,
  score,
  sub,
}: {
  label: string;
  value?: ReactNode;
  score?: number | null;
  sub?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="panel p4 stat-tile" role="group" aria-labelledby={id}>
      <p className="kpi-label" id={id}>
        {label}
      </p>
      {score !== undefined ? <ScoreValue score={score} /> : <p className="kpi-value">{value}</p>}
      {sub && <p className="kpi-sub">{sub}</p>}
    </div>
  );
}

export type BarDatum = { key: string; emoji?: string; label: string; count: number | null };

// DomainBars pattern: label column, 8px track filled `teal` by share, count.
// Rendered as a list so screen readers hear "Great, 4" per row. A null count
// means the server suppressed it — shown as such, never as 0.
export function Bars({ data, total, labelledBy }: { data: BarDatum[]; total: number; labelledBy?: string }) {
  const { t } = useI18n();
  return (
    <ul className="bars" aria-labelledby={labelledBy}>
      {data.map((d) => {
        const share = d.count !== null && total > 0 ? (d.count / total) * 100 : 0;
        return (
          <li className="bar-row" key={d.key}>
            <span className="bar-label">
              {d.emoji && <span aria-hidden="true">{d.emoji} </span>}
              {d.label}
            </span>
            <span className="track" aria-hidden="true">
              <i style={{ width: `${share}%`, background: 'var(--teal)' }} />
            </span>
            <span className={`bar-count${d.count === null ? ' hidden' : ''}`}>
              <VisuallyHidden>: </VisuallyHidden>
              {d.count === null ? t['results.hidden'] : d.count}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// "Suppression is explained, not hidden."
export function Suppressed({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="suppressed">
      <p className="suppressed-title">{title}</p>
      <p>{children}</p>
    </div>
  );
}
